import * as firebaseClient from '@/services/notifications/firebase';
import api from '@/services/api';
import ls from '@/helpers/localStorage';
import constants from '@/constants';

export const DEFAULT_ORDER_BOOK = {
  noOrders: 0,
  yesOrders: 0,
};

function makeTradeOptions() {
  return {
    contractId: null,
    operationType: 'buy', // buy | sell
    operationValue: 'yes', // yes | no
    pricePerShare: null,
    quantity: constants.DEFAULT_QUANTITY,
    skipConfirmation: ls.get('skipConfirmation', false),
    defaultTab: null,
  };
}

export default {
  namespaced: true,
  state: {
    orderBook: null,
    contractShares: [],
    contractOffers: [],
    tradeOptions: makeTradeOptions(),
    tradeSuccess: false,
    keyboardDoubleSelected: false,
    cancelToggle: false,
    tradeOperationToggle: null,
    confirmationToggle: false,
  },
  mutations: {
    setOrderBook(state, payload) {
      state.orderBook = payload;
    },
    clearOrderBook(state) {
      state.orderBook = null;
    },
    setContractShares(state, payload) {
      state.contractShares = payload;
    },
    setContractOffers(state, payload) {
      state.contractOffers = payload;
    },
    setTradeOptions(state, payload) {
      if (
        state.tradeOptions !== null &&
        payload !== null &&
        state.tradeOptions.contractId === payload.contractId &&
        state.tradeOptions.operationType === payload.operationType &&
        state.tradeOptions.operationValue === payload.operationValue
      ) {
        state.tradeOptions = {
          ...state.tradeOptions,
          ...payload,
        };
      } else {
        const defaults = makeTradeOptions();
        let pricePerShare = defaults.pricePerShare;
        if (payload && state.tradeOptions.contractId === payload.contractId) {
          pricePerShare = state.tradeOptions.pricePerShare;
        }

        state.tradeOptions = {
          ...defaults,
          ...payload,
          pricePerShare,
        };
      }
    },
    setTradeSuccess(state, flag) {
      state.tradeSuccess = flag;
    },
    setTradeQuantity(state, quantity) {
      state.tradeOptions = { ...state.tradeOptions, quantity: parseInt(quantity, 10) || null };
    },
    setTradeDefaultTab(state, tab) {
      state.tradeOptions = { ...state.tradeOptions, defaultTab: tab };
    },
    setTradePrice(state, pricePerShare) {
      const min = 1;
      const max = 99;
      let price = parseInt(pricePerShare, 10);

      if (price < min) {
        price = min;
      }
      if (price > max) {
        price = max;
      }
      state.tradeOptions = { ...state.tradeOptions, pricePerShare: price };
    },
    setTradeSkipConfirmation(state, skipConfirmation) {
      const value = !!skipConfirmation;
      const expiresAt = 31536000; // 1 year
      ls.set('skipConfirmation', value, expiresAt);
      state.tradeOptions = { ...state.tradeOptions, skipConfirmation: value };
    },
    updateKeyboardDoubleSelected(state, selected) {
      state.keyboardDoubleSelected = selected;
    },
    setCancelToggle(state, flag) {
      state.cancelToggle = flag;
    },
    setConfirmationToggle(state, flag) {
      state.confirmationToggle = flag;
    },
    toggleTradeOperation(state, side) {
      state.tradeOperationToggle = side;
    },
  },
  actions: {
    confirmTrade({ commit }, payload) {
      return api.trade.postConfirmTrade(payload);
    },
    submitTrade({ commit }, payload) {
      return api.trade.postSubmitTrade(payload);
    },
    getOrderBook({ commit }, contractId) {
      return api.trade.getOrderBook(contractId)
        .then((response) => {
          commit('setOrderBook', response.data);
          return response.data;
        });
    },
    updateOrderBookPeriodically({ commit }, contractId) {
      firebaseClient.addOrderBookListener(contractId, (data) => {
        commit('setOrderBook', data);
      }, Object.create(DEFAULT_ORDER_BOOK));
    },
    clearOrderBookUpdates({ commit }, contractId) {
      firebaseClient.clearOrderBookListeners(contractId);
    },
    clearOrderBook({ commit }) {
      commit('clearOrderBook');
    },
    getContractShares({ commit }, contractId) {
      if (contractId === null) {
        commit('setContractShares', []);
        return Promise.resolve([]);
      }

      return api.profile.getContractShares(contractId)
        .then((response) => {
          commit('setContractShares', response.data.shares);
          return response.data.shares;
        });
    },
    getContractOffers({ commit }, contractId) {
      if (contractId === null) {
        commit('setContractOffers', []);
        return Promise.resolve([]);
      }

      return api.profile.getContractOffers(contractId)
        .then((response) => {
          commit('setContractOffers', response.data.offers);
          return response.data.offers;
        });
    },
    cancelAllOffers({ commit }, payload) {
      const contractId = payload.contractId;
      delete payload.contractId;
      return api.trade.postCancelAllOffers(contractId, payload)
        .then((response) => {
          commit('setCancelToggle', true);
          return response.data;
        });
    },
    cancelOffer({ commit }, offerId) {
      return api.trade.postCancelOffer(offerId)
        .then((response) => {
          commit('setCancelToggle', true);
          return response.data;
        });
    },
    setCancelToggle({ commit }, flag) {
      commit('setCancelToggle', flag);
    },
    setConfirmationToggle({ commit }, flag) {
      commit('setConfirmationToggle', flag);
    },
    setTradeOptions({ commit }, payload) {
      commit('setTradeOptions', payload);
    },
    setTradeQuantity({ commit }, quantity) {
      commit('setTradeQuantity', quantity);
    },
    setTradePrice({ commit }, pricePerShare) {
      commit('setTradePrice', pricePerShare);
    },
    setTradeDefaultTab({ commit }, tab) {
      commit('setTradeDefaultTab', tab);
    },
    setTradeSuccess({ commit }, flag) {
      commit('setTradeSuccess', flag);
    },
    setTradeSkipConfirmation({ commit }, skipConfirmation) {
      commit('setTradeSkipConfirmation', skipConfirmation);
    },
    toggleTradeOperation({ commit }, side) {
      commit('toggleTradeOperation', side);
    },
    updateKeyboardDoubleSelected({ commit }, selected) {
      commit('updateKeyboardDoubleSelected', selected);
    },
    getOrderBookTradeInfo({ commit }, { contractId, tradeType, price }) {
      return api.trade.getOrderBookTradeInfo(contractId, tradeType, price)
        .then(response => response.data);
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/trade.js